import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import type { SubmitErrorHandler, SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { v4 as uuid } from 'uuid';

import { OfferForm } from './OfferForm';
import { OfferModalShell } from './OfferModalShell';
import type { SchemaManualOfferForm } from '~/features/leadDetails/schemas/manualOfferForm.schema';
import { schemaManualOfferForm } from '~/features/leadDetails/schemas/manualOfferForm.schema';
import { setQueryPosition } from '~/store/queryPositionStore';
import { appendPositionToQueryKey } from '~/utils/appendPositionToQueryKey';
import { queryKeys } from '~/utils/queryKeys';
import uploadFiles from '~/utils/uploadFiles';

type Props = {
    onClose: () => void;
    open: boolean;
};

export const CreateOfferModal: React.FC<Props> = ({ onClose, open }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { useParams } = getRouteApi('/_app/leads_/details/$leadId');
    const { leadId } = useParams();
    const methods = useForm<SchemaManualOfferForm>({
        resolver: zodResolver(schemaManualOfferForm),
    });

    // The modal stays mounted, so its form state must be cleared explicitly when it closes
    useEffect(() => {
        if (!open) methods.reset();
    }, [open, methods]);

    const createOffer: SubmitHandler<SchemaManualOfferForm> = async (data) => {
        const offerId = uuid();
        const { commissionAmount: _commissionAmount, ...offer } = data;
        const queryKeyGetOffers = queryKeys.getAcceptedOffers(leadId);
        const queryKeyGetOfferCounts = queryKeys.getAcceptedOfferCounts(leadId);

        const formData = new FormData();
        if (data.file) formData.append('0', data.file);
        formData.append('jsonData', JSON.stringify({ ...offer, id: offerId }));

        const response: Response = await uploadFiles(`/offer?aggregateId=${leadId}&offerId=${offerId}`, {
            body: formData,
            credentials: 'include',
            method: 'post',
        });

        if (response.status !== 200) {
            toast.error(t('OFFER_MANAGEMENT.TOASTS.STATUS_CREATION_FAILED'));
            return;
        }

        toast.success(t('OFFER_MANAGEMENT.TOASTS.STATUS_CREATION_SUCCESS'));

        const position =
            response.body && 'position' in response.body && typeof response.body.position === 'number'
                ? response.body.position
                : undefined;

        if (position) {
            setQueryPosition(JSON.stringify(queryKeyGetOffers), position);
            setQueryPosition(JSON.stringify(queryKeyGetOfferCounts), position);
        }

        await queryClient.invalidateQueries({
            queryKey: appendPositionToQueryKey.custom(queryKeyGetOffers, position),
        });
        await queryClient.invalidateQueries({
            queryKey: appendPositionToQueryKey.custom(queryKeyGetOfferCounts, position),
        });

        methods.reset();
        onClose();
    };

    const handleError: SubmitErrorHandler<SchemaManualOfferForm> = () => {
        toast.error(t('OFFER_MANAGEMENT.TOASTS.STATUS_CREATION_FAILED'));
    };

    return (
        <FormProvider {...methods}>
            <OfferModalShell
                description={t('OFFER_MANAGEMENT.MANUAL_OFFER.CREATE_DESCRIPTION')}
                isSubmitting={methods.formState.isSubmitting}
                onClose={onClose}
                onSubmit={methods.handleSubmit(createOffer, handleError)}
                open={open}
                title={t('OFFER_MANAGEMENT.MANUAL_OFFER.BUTTON_TITLE')}
            >
                <OfferForm />
            </OfferModalShell>
        </FormProvider>
    );
};
